"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import type { ControlSectionQueryResult } from "@/sanity/schemas/lib/queries";

interface ControlSectionProps {
    sanityContent: ControlSectionQueryResult;
}

export function ControlSection({ sanityContent }: ControlSectionProps) {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((current) => (current + 1) % sanityContent.highlights.length);
        }, 5000);

        return () => clearInterval(timer);
    }, [sanityContent.highlights.length]);

    return (
        <section className="bg-system-bg py-20 pt-8 md:py-16">
            <div className="mx-auto max-w-7xl min-[0px]:px-5 min-[768px]:px-20 min-[1440px]:px-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between  min-[1440px]:gap-35">
                    <div className="flex-1 max-w-lg">
                        <p className="text-sm text-system-subtitle/70 mb-2">
                            {sanityContent.eyebrow}
                        </p>
                        <h2 className="text-h2-mobile font-semibold leading-120 text-ink md:text-h2-desktop mb-12 md:whitespace-nowrap">
                            {sanityContent.headline.prefix}{" "}
                            <span className="bg-linear-to-r from-ink via-system-blue to-ink bg-clip-text text-transparent">
                                {sanityContent.headline.highlight}
                            </span>
                        </h2>

                        <div className="relative border-l border-system-glow/30 pl-8 ml-2 flex flex-col gap-6">
                            {sanityContent.highlights.map((item, index) => {
                                const isActive = index === activeIndex;
                                const isFirst = index === 0;
                                const isLast = index === sanityContent.highlights.length - 1;

                                return (
                                    <div key={index} className="relative">
                                        {isActive && (
                                            <div className="absolute -left-8.25 top-0 bottom-0 w-0.5 bg-yellow-2" />
                                        )}

                                        {isFirst && (
                                            <div
                                                className={`absolute -left-9.25 top-0 h-2 w-2 rounded-full transition-colors duration-300 ${
                                                    isActive
                                                        ? "bg-yellow-2"
                                                        : "bg-system-subtitle/30"
                                                }`}
                                            />
                                        )}

                                        {isLast && (
                                            <div
                                                className={`absolute -left-9.25 bottom-0 h-2 w-2 rounded-full transition-colors duration-300 ${
                                                    isActive
                                                        ? "bg-yellow-2"
                                                        : "bg-system-subtitle/30"
                                                }`}
                                            />
                                        )}

                                        <h4
                                            className={`font-semibold transition-colors duration-300 ${
                                                isActive ? "text-ink" : "text-ink/60"
                                            }`}
                                        >
                                            {item.heading}
                                        </h4>

                                        <div
                                            className={`grid transition-all duration-500 ease-in-out ${
                                                isActive
                                                    ? "grid-rows-[1fr] opacity-100 mt-2"
                                                    : "grid-rows-[0fr] opacity-0 mt-0"
                                            }`}
                                        >
                                            <div className="overflow-hidden">
                                                <p className="text-sm text-system-subtitle pb-1">
                                                    {item.body}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="hidden min-[1000px]:flex flex-1 items-center justify-center">
                        <div className="hidden md:flex flex-1 items-center justify-center">
                            <div className="relative w-full max-w-[420px] min-[1300px]:max-w-[583px] aspect-square flex items-center justify-center transition-all duration-500">
                                <Image
                                    src="/section-illustration/start-frame.svg"
                                    alt="start-frame"
                                    width={100}
                                    height={100}
                                    priority
                                    className="relative z-10 w-[75px] h-[75px] min-[1300px]:w-[100px] min-[1300px]:h-[100px] transition-all duration-500"
                                />

                                <Image
                                    src="/section-illustration/Ellipse-one.svg"
                                    alt="Ellipse-one"
                                    width={278}
                                    height={278}
                                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] min-[1300px]:w-[278px] min-[1300px]:h-[278px] transition-all duration-700 ease-out ${
                                        activeIndex >= 1
                                            ? "opacity-100 scale-100"
                                            : "opacity-0 scale-75 pointer-events-none"
                                    }`}
                                />

                                <Image
                                    src="/section-illustration/Ellipse-two.svg"
                                    alt="Ellipse-two"
                                    width={420}
                                    height={420}
                                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] min-[1300px]:w-[420px] min-[1300px]:h-[420px] transition-all duration-700 ease-out ${
                                        activeIndex >= 2
                                            ? "opacity-100 scale-100"
                                            : "opacity-0 scale-75 pointer-events-none"
                                    }`}
                                />

                                <Image
                                    src="/section-illustration/Ellipse-three.svg"
                                    alt="Ellipse-three"
                                    width={560}
                                    height={560}
                                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] min-[1300px]:w-[560px] min-[1300px]:h-[560px] transition-all duration-700 ease-out ${
                                        activeIndex >= 3
                                            ? "opacity-100 scale-100"
                                            : "opacity-0 scale-75 pointer-events-none"
                                    }`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
